/**
 * theme.ts — "Calm Premium" design tokens for ColorDrop.
 *
 * This is the SINGLE SOURCE OF TRUTH for colour, type, radius, shadow and motion.
 * No other file should hardcode a colour hex. Import `theme` and use the tokens.
 *
 * Art direction: the cozy gallery calm of Happy Color, elevated with premium
 * reward moments. NO casino neon. Win intensity scales ONLY with win size.
 */

/** Convert a #rrggbb string to a 0xRRGGBB number for Pixi tint/fill APIs. */
export function hex(c: string): number {
  return parseInt(c.replace('#', ''), 16);
}

/** Linearly mix two hex colours. t=0 -> a, t=1 -> b. */
export function mix(a: string, b: string, t: number): string {
  const pa = a.replace('#', '');
  const pb = b.replace('#', '');
  const ar = parseInt(pa.slice(0, 2), 16);
  const ag = parseInt(pa.slice(2, 4), 16);
  const ab = parseInt(pa.slice(4, 6), 16);
  const br = parseInt(pb.slice(0, 2), 16);
  const bg = parseInt(pb.slice(2, 4), 16);
  const bb = parseInt(pb.slice(4, 6), 16);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${[r, g, bl].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/** Lighten / darken a hex by a factor (0..1). amt>0 lighten, amt<0 darken. */
export function shade(c: string, amt: number): string {
  return amt >= 0 ? mix(c, '#ffffff', amt) : mix(c, '#000000', -amt);
}

export const palette = {
  // Backgrounds
  bgLight1: '#fbf6f1',
  bgLight2: '#f4ecfb',
  bgLight3: '#eef0fd',
  bgDark1: '#241046',
  bgDark2: '#140a26',

  // Text
  ink: '#3a2c52',
  muted: '#9385ad',
  hint: '#b0a0c8',

  // Brand teal (active / painting / progress)
  teal: '#2bc0a8',
  tealBright: '#37d6b4',

  // Win gold (coins, buy CTA, big wins)
  gold1: '#ffd36b',
  gold2: '#f3a519',
  goldText: '#7a4a00',

  // FOMO purple (picture bonus, meta)
  purple: '#8b5cf6',
  purpleDeep: '#7c3aed',

  // Coral accent
  coral: '#ff7a59',

  // Surfaces
  white: '#ffffff',
  // Drop-shadow tint (violet-grey) — referenced by theme.shadow so a brand-hue change reaches it
  shadowTint: '#5a3c8c',
} as const;

export const theme = {
  palette,

  /** Colours as 0xRRGGBB numbers, ready for Pixi fill/tint. */
  color: {
    bgLight1: hex(palette.bgLight1),
    bgLight2: hex(palette.bgLight2),
    bgLight3: hex(palette.bgLight3),
    bgDark1: hex(palette.bgDark1),
    bgDark2: hex(palette.bgDark2),
    ink: hex(palette.ink),
    muted: hex(palette.muted),
    hint: hex(palette.hint),
    teal: hex(palette.teal),
    tealBright: hex(palette.tealBright),
    gold1: hex(palette.gold1),
    gold2: hex(palette.gold2),
    goldText: hex(palette.goldText),
    purple: hex(palette.purple),
    purpleDeep: hex(palette.purpleDeep),
    coral: hex(palette.coral),
    white: hex(palette.white),
    // Frosted-glass surface tuning — single source so HUD / dock / panels share one depth language
    glassAlpha: 0.82,
    glassStrokeAlpha: 0.9,
  },

  /** Gradients expressed as ordered stop arrays (hex strings). */
  gradient: {
    galleryBg: [palette.bgLight1, palette.bgLight2, palette.bgLight3],
    eveningBg: [palette.bgDark1, palette.bgDark2],
    gold: [palette.gold1, palette.gold2],
    teal: [palette.tealBright, palette.teal],
    purple: [palette.purple, palette.purpleDeep],
  },

  /** Corner radii. */
  radius: {
    card: 20,
    cardSm: 14,
    pill: 999,
    chip: 12,
  },

  /** Soft shadow recipe (colour + offset + blur), used by drop-shadow filters. */
  shadow: {
    soft: { color: hex(palette.shadowTint), alpha: 0.16, blur: 22, offsetY: 8 },
    lift: { color: hex(palette.shadowTint), alpha: 0.22, blur: 30, offsetY: 12 },
  },

  /** Typography. */
  font: {
    family: "'Baloo 2', 'Nunito', system-ui, sans-serif",
    familyName: 'Baloo 2',
    weight: { reg: '500', med: '600', bold: '700', black: '800' },
  },

  /** Spacing scale (px). */
  space: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 },

  /** Motion durations (ms) + easing identifiers (see util/ease.ts). */
  motion: {
    fast: 140,
    base: 240,
    slow: 420,
    crossfade: 260,
    inkBloom: 320,
  },

  /** Reward tier thresholds, expressed as multiple of stake. */
  tiers: {
    big: 10, // >=10x  -> "Big Win"
    mega: 50, // >=50x  -> "Mega Win"
    legendary: 200, // >=200x -> "Legendary"
  },
} as const;

export type Theme = typeof theme;
export default theme;
